/**
 * 1.4.5: 成员管理标签页组件
 * 账号中心的成员管理 Tab，整合成员列表、邀请、角色变更等功能
 * 遵循 YUI 主题设计规范
 * 1.4.5: 将原生 confirm 弹窗替换为符合 YUI 设计规范的自定义 ConfirmModal
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, UserPlus, Loader2, X, Clock, AlertCircle, Users, Minus, Plus } from 'lucide-react';
import { ConfirmModal } from '../ConfirmModal';
import { toast } from 'react-hot-toast';
import {
  getCurrentOrganization,
  getOrganizationDetails,
  removeMember,
  updateMemberRole,
  getOrganizationInvitations,
  cancelInvitation,
  type UserOrganization,
  type OrganizationDetails,
  type MemberRole,
} from '../../services/organizationService';
import { getOrganizationSubscription, updateSubscriptionQuantity, formatPrice } from '../../services/billingService'; // 1.4.5: 使用组织ID获取订阅
import type { SubscriptionInfo } from '../../types/billing';
import { MemberQuotaBar } from '../MemberQuotaBar';
import { MemberCard } from '../MemberCard';
import { InviteMemberModal } from '../InviteMemberModal';
import { InviteLinkModal } from '../InviteLinkModal';
import { supabase } from '../../lib/supabase';

interface Invitation {
  id: string;
  email?: string;
  role: MemberRole;
  status: string;
  expires_at: string;
  use_count?: number;
  max_uses?: number;
}

export function MembersTab() {
  const { t } = useTranslation();
  
  const [organization, setOrganization] = useState<UserOrganization | null>(null);
  const [details, setDetails] = useState<OrganizationDetails | null>(null);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  
  // 弹窗状态
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  
  // 1.4.1: 调整人数弹窗状态
  const [showQuantityModal, setShowQuantityModal] = useState(false);
  const [newQuantity, setNewQuantity] = useState(2);
  const [isUpdatingQuantity, setIsUpdatingQuantity] = useState(false);
  
  // 1.4.5: 确认弹窗状态 - 取消邀请
  const [showCancelInviteConfirm, setShowCancelInviteConfirm] = useState(false);
  const [pendingCancelInvitationId, setPendingCancelInvitationId] = useState<string | null>(null);
  
  // 1.4.5: 确认弹窗状态 - 移除成员
  const [showRemoveMemberConfirm, setShowRemoveMemberConfirm] = useState(false);
  const [pendingRemoveMember, setPendingRemoveMember] = useState<{ userId: string; email: string } | null>(null);

  // 加载数据
  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      // 获取当前用户 ID
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setCurrentUserId(user.id);
      }

      const { organization: org } = await getCurrentOrganization();
      if (org) {
        setOrganization(org);
        
        const { details: orgDetails } = await getOrganizationDetails(org.id);
        setDetails(orgDetails);
        
        const { invitations: inv } = await getOrganizationInvitations(org.id);
        setInvitations(inv || []);
        
        // 1.4.5: 使用组织ID获取订阅信息，确保member看到正确的订阅状态
        const { subscription: subData } = await getOrganizationSubscription(org.id);
        setSubscription(subData);
      }
    } catch (err: any) {
      toast.error(err.message || t('loadFailed', '加载失败'));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // 1.4.5: 移除成员 - 显示确认弹窗
  const handleRemoveMember = (userId: string, email: string) => {
    if (!organization) return;
    setPendingRemoveMember({ userId, email });
    setShowRemoveMemberConfirm(true);
  };

  // 1.4.5: 确认移除成员
  const confirmRemoveMember = async (confirmed: boolean) => {
    setShowRemoveMemberConfirm(false);
    if (!confirmed || !organization || !pendingRemoveMember) {
      setPendingRemoveMember(null);
      return;
    }

    try {
      const { result, error: apiError } = await removeMember(organization.id, pendingRemoveMember.userId);
      if (apiError) {
        toast.error(apiError.message);
        return;
      }
      toast.success(t('memberRemoved', '成员已移除'));
      loadData();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setPendingRemoveMember(null);
    }
  };

  // 变更角色
  const handleRoleChange = async (userId: string, newRole: MemberRole) => {
    if (!organization) return;

    try {
      const { result, error: apiError } = await updateMemberRole(organization.id, userId, newRole);
      if (apiError) {
        toast.error(apiError.message);
        return;
      }
      toast.success(t('roleUpdated', '角色已更新'));
      loadData();
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  // 1.4.5: 取消邀请 - 显示确认弹窗
  const handleCancelInvitation = (invitationId: string) => {
    setPendingCancelInvitationId(invitationId);
    setShowCancelInviteConfirm(true);
  };

  // 1.4.5: 确认取消邀请
  const confirmCancelInvitation = async (confirmed: boolean) => {
    setShowCancelInviteConfirm(false);
    if (!confirmed || !pendingCancelInvitationId) {
      setPendingCancelInvitationId(null);
      return;
    }

    try {
      const { success: ok, error: apiError } = await cancelInvitation(pendingCancelInvitationId);
      if (apiError) {
        toast.error(apiError.message);
        return;
      }
      toast.success(t('invitationCanceled', '邀请已取消'));
      loadData();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setPendingCancelInvitationId(null);
    }
  };

  // 格式化过期时间
  const formatExpiresAt = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // 1.4.1: 打开调整人数弹窗
  const handleOpenQuantityModal = () => {
    setNewQuantity(subscription?.purchased_seats || 2);
    setShowQuantityModal(true);
  };

  // 1.4.1: 更新人数
  const handleUpdateQuantity = async () => {
    if (!organization) return;
    
    const currentQuantity = subscription?.purchased_seats || 2;
    if (newQuantity === currentQuantity) {
      setShowQuantityModal(false);
      return;
    }

    setIsUpdatingQuantity(true);
    
    const { result, error } = await updateSubscriptionQuantity(
      organization.id,
      newQuantity
    );
    
    if (error) {
      toast.error(error.message);
    } else if (result) {
      toast.success(result.message);
      setShowQuantityModal(false);
      // 刷新数据
      loadData();
    }
    
    setIsUpdatingQuantity(false);
  };

  // 1.4.1: 计算人数变化的费用预估
  const calculateQuantityChange = () => {
    const currentQuantity = subscription?.purchased_seats || 2;
    const diff = newQuantity - currentQuantity;
    const pricePerUser = 2300; // Standard 单价
    
    if (diff > 0) {
      return {
        type: 'increase' as const,
        diff,
        estimatedCost: diff * pricePerUser,
        message: t('increaseSeatsMessage', '增加 {{count}} 个席位，将立即按比例收费约 {{price}}', {
          count: diff,
          price: formatPrice(diff * pricePerUser)
        })
      };
    } else if (diff < 0) {
      return {
        type: 'decrease' as const,
        diff: Math.abs(diff),
        estimatedCost: 0,
        message: t('decreaseSeatsMessage', '减少 {{count}} 个席位，将从下个计费周期开始生效', {
          count: Math.abs(diff)
        })
      };
    }
    return null;
  };

  const isOwner = organization?.role === 'owner';
  const pendingInvitations = invitations.filter(i => i.status === 'pending');

  // 加载中状态
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <span className="inline-block text-primary font-semibold yui-loading-animation text-2xl">YUI</span>
      </div>
    );
  }

  // 无组织信息
  if (!organization || !details) {
    return (
      <div className="text-center py-16">
        <div className="flex flex-col items-center gap-3 text-gray-500">
          <AlertCircle className="w-12 h-12" />
          <p>{t('noOrganizationFound', '未找到组织信息')}</p>
        </div>
      </div>
    );
  }

  // 1.4.1: 计算成员配额 - 使用已购买席位数（Standard套餐）或最大成员数
  const purchasedSeats = subscription?.purchased_seats;
  const maxMembers = purchasedSeats && purchasedSeats > 0 
    ? purchasedSeats 
    : (organization.max_members || 1);
  const currentMembers = organization.current_members || 0;

  // 1.5.5: 检查席位是否已满（边界问题修复）
  const isQuotaFull = currentMembers >= maxMembers;

  return (
    <div className="space-y-6">
      {/* 成员配额 */}
      <MemberQuotaBar
        currentMembers={currentMembers}
        maxMembers={maxMembers}
        showAdjustButton={isOwner && organization.plan_type === 'standard'}
        onAdjust={handleOpenQuantityModal}
      />

      {/* 操作按钮（仅 Owner 显示） */}
      {/* 1.5.5: 席位已满时禁用邀请按钮 */}
      {isOwner && (
        <div className="flex flex-col gap-3">
          <div className="flex gap-3">
            <button
              onClick={() => setShowLinkModal(true)}
              disabled={isQuotaFull}
              className={`flex items-center gap-2 px-4 py-2.5 border-2 rounded-xl transition-colors ${
                isQuotaFull
                  ? 'border-gray-300 text-gray-400 cursor-not-allowed'
                  : 'border-primary text-primary hover:bg-primary/5'
              }`}
              title={isQuotaFull ? t('quotaFullCannotInvite', '席位已满，无法邀请新成员') : ''}
            >
              <Link className="w-4 h-4" />
              {t('generateInviteLink', '生成邀请链接')}
            </button>
            <button
              onClick={() => setShowInviteModal(true)}
              disabled={isQuotaFull}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl transition-opacity ${
                isQuotaFull
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-primary to-primary-dark text-white hover:opacity-90'
              }`}
              title={isQuotaFull ? t('quotaFullCannotInvite', '席位已满，无法邀请新成员') : ''}
            >
              <UserPlus className="w-4 h-4" />
              {t('inviteMember', '邀请成员')}
            </button>
          </div>
          {/* 1.5.5: 席位已满时显示提示 */}
          {isQuotaFull && (
            <p className="text-sm text-orange-600 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {t('quotaFullInviteDisabled', '席位已满，请先调整人数或移除成员后再邀请')}
            </p>
          )}
        </div>
      )}

      {/* 成员列表 */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">{t('memberList', '成员列表')}</h3>
        </div>
        
        <div className="p-4 space-y-3">
          {details.members?.map((member) => (
            <MemberCard
              key={member.id}
              member={{
                id: member.id,
                user_id: member.user_id,
                email: member.email || '',
                display_name: member.display_name,
                role: member.role as MemberRole,
                status: member.status || 'active',
                joined_at: member.joined_at,
              }}
              isOwner={isOwner}
              isCurrentUser={member.user_id === currentUserId}
              onRoleChange={handleRoleChange}
              onRemove={handleRemoveMember}
            />
          ))}
          
          {(!details.members || details.members.length === 0) && (
            <div className="text-center py-8 text-gray-500">
              {t('noMembers', '暂无成员')}
            </div>
          )}
        </div>
      </div>

      {/* 待处理邀请（仅 Owner 且有待处理邀请时显示） */}
      {isOwner && pendingInvitations.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <h3 className="font-semibold text-gray-900">{t('pendingInvitations', '待处理邀请')}</h3>
          </div>
          
          <div className="p-4 space-y-3">
            {pendingInvitations.map((invitation) => (
              <div
                key={invitation.id}
                className="bg-gray-50 rounded-xl p-4 flex items-center justify-between"
              >
                <div className="flex items-center gap-4">
                  {/* 邮件图标 */}
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                    <span className="text-purple-600 text-lg">✉️</span>
                  </div>
                  
                  <div>
                    <p className="font-medium text-gray-900">
                      {invitation.email || t('inviteLink', '邀请链接')}
                    </p>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Clock className="w-3 h-3" />
                      <span>{t('expiresAt', '过期时间')}：{formatExpiresAt(invitation.expires_at)}</span>
                      {invitation.max_uses && (
                        <span className="text-gray-400">
                          · {t('usageCount', '使用次数')}：{invitation.use_count || 0}/{invitation.max_uses}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm font-medium">
                    {t('pending', '待接受')}
                  </span>
                  <button
                    onClick={() => handleCancelInvitation(invitation.id)}
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    title={t('cancelInvitation', '取消邀请')}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 邀请成员弹窗 */}
      {showInviteModal && organization && (
        <InviteMemberModal
          organizationId={organization.id}
          onClose={() => setShowInviteModal(false)}
          onSuccess={() => {
            setShowInviteModal(false);
            toast.success(t('inviteSent', '邀请已发送'));
            loadData();
          }}
        />
      )}

      {/* 生成邀请链接弹窗 */}
      {showLinkModal && organization && (
        <InviteLinkModal
          organizationId={organization.id}
          onClose={() => setShowLinkModal(false)}
        />
      )}

      {/* 1.4.5: 取消邀请确认弹窗 */}
      <ConfirmModal
        isOpen={showCancelInviteConfirm}
        onClose={confirmCancelInvitation}
        title={t('cancelInvitation', '取消邀请')}
        description={t('confirmCancelInvitation', '确定要取消这个邀请吗？')}
        isDelete={true}
      />

      {/* 1.4.5: 移除成员确认弹窗 */}
      <ConfirmModal
        isOpen={showRemoveMemberConfirm}
        onClose={confirmRemoveMember}
        title={t('removeMember', '移除成员')}
        description={t('confirmRemoveMember', { email: pendingRemoveMember?.email || '' })}
        isDelete={true}
      />

      {/* 1.4.1: 调整人数弹窗 */}
      {showQuantityModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full mx-4 overflow-hidden">
            {/* 弹窗标题 */}
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                {t('adjustTeamSize', '调整团队人数')}
              </h3>
            </div>

            {/* 弹窗内容 */}
            <div className="px-6 py-6">
              {/* 当前人数 */}
              <div className="text-center mb-6">
                <p className="text-sm text-gray-500 mb-2">
                  {t('currentSeats', '当前人数')}: {subscription?.purchased_seats || 2} {t('seats', '人')}
                </p>
              </div>

              {/* 人数选择器 */}
              {/* 1.5.5: 最小值改为当前成员数或2（取较大值） */}
              <div className="flex items-center justify-center gap-4 mb-6">
                <button
                  onClick={() => setNewQuantity(Math.max(currentMembers, 2, newQuantity - 1))}
                  disabled={newQuantity <= Math.max(currentMembers, 2)}
                  className="w-12 h-12 rounded-full bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
                >
                  <Minus className="w-5 h-5 text-gray-600" />
                </button>
                
                <div className="text-center">
                  <p className="text-4xl font-bold text-gray-900">{newQuantity}</p>
                  <p className="text-sm text-gray-500">{t('seats', '人')}</p>
                </div>
                
                <button
                  onClick={() => setNewQuantity(Math.min(50, newQuantity + 1))}
                  disabled={newQuantity >= 50}
                  className="w-12 h-12 rounded-full bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
                >
                  <Plus className="w-5 h-5 text-gray-600" />
                </button>
              </div>

              {/* 费用变化提示 */}
              {calculateQuantityChange() && (
                <div className={`p-4 rounded-lg mb-6 ${
                  calculateQuantityChange()?.type === 'increase' 
                    ? 'bg-blue-50 border border-blue-100' 
                    : 'bg-orange-50 border border-orange-100'
                }`}>
                  <p className={`text-sm ${
                    calculateQuantityChange()?.type === 'increase' 
                      ? 'text-blue-700' 
                      : 'text-orange-700'
                  }`}>
                    {calculateQuantityChange()?.message}
                  </p>
                  {calculateQuantityChange()?.type === 'increase' && (
                    <p className="text-xs text-blue-500 mt-1">
                      {t('proratedBilling', '按当前计费周期剩余天数按比例计算')}
                    </p>
                  )}
                </div>
              )}

              {/* 1.5.5: 最小席位数提示 */}
              {currentMembers > 2 && newQuantity === currentMembers && (
                <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg mb-6">
                  <p className="text-xs text-gray-600 flex items-center gap-1">
                    <AlertCircle className="w-3.5 h-3.5" />
                    {t('minSeatsInfo', '最少需要 {{count}} 个席位（当前成员数）', { count: currentMembers })}
                  </p>
                </div>
              )}

              {/* 月费预估 */}
              <div className="text-center text-sm text-gray-500">
                {t('newMonthlyFee', '调整后月费')}: 
                <span className="font-semibold text-gray-900 ml-1">
                  {formatPrice(newQuantity * 2300)}
                </span>
              </div>
            </div>

            {/* 弹窗按钮 */}
            <div className="px-6 py-4 bg-gray-50 flex gap-3">
              <button
                onClick={() => setShowQuantityModal(false)}
                disabled={isUpdatingQuantity}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
              >
                {t('cancel', '取消')}
              </button>
              <button
                onClick={handleUpdateQuantity}
                disabled={isUpdatingQuantity || newQuantity === (subscription?.purchased_seats || 2)}
                className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isUpdatingQuantity ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  t('confirm', '确认')
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
